package com.example.mystybag.login.logic

import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.byteArrayFromFile
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.security.Security
import com.example.mystybag.login.model.LoginDetailsRequiredField
import com.example.mystybag.login.model.MerchantRegistrationAddLoginDetailsUIState
import com.example.mystybag.login.model.PasswordRequirements
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GenerateMerchantVerificationCodeRequestModel
import kotlinx.coroutines.launch

class MerchantRegistrationAddLoginDetailsViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep4
) : ChildViewModel<MerchantRegistrationAddLoginDetailsUIState>(context, MerchantRegistrationAddLoginDetailsUIState()) {

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig

    init {
        updateState {
            it.copy(
                email = sharedConfig.email,
                isLoading = false
            )
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
    }

    fun updateEmail(email: String) {
        updateState {
            it.copy(
                email = email,
                requiredFieldErrors = it.requiredFieldErrors - LoginDetailsRequiredField.EMAIL
            )
        }
        sharedConfig.email = email
    }

    fun updatePassword(password: String) {
        val requirements = PasswordRequirements(
            hasMinLength = password.length >= 8,
            hasNumber = password.any { it.isDigit() },
            hasLetter = password.any { it.isLetter() }
        )
        updateState {
            it.copy(
                password = password,
                passwordRequirements = requirements,
                requiredFieldErrors = it.requiredFieldErrors - LoginDetailsRequiredField.PASSWORD
            )
        }
        sharedConfig.password = password
    }
    
    override fun onBackButtonClicked() {
        // Update shared config with current state before going back
        val state = uiState.value
        sharedConfig.email = state.email
        sharedConfig.password = state.password
        
        // Update current config in stack
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        
        super.onBackButtonClicked()
    }

    fun togglePasswordVisibility() {
        updateState { it.copy(passwordVisible = !it.passwordVisible) }
    }

    fun updateConfirmPassword(confirmPassword: String) {
        updateState {
            it.copy(
                confirmPassword = confirmPassword,
                requiredFieldErrors = it.requiredFieldErrors - LoginDetailsRequiredField.CONFIRM_PASSWORD
            )
        }
    }

    fun toggleConfirmPasswordVisibility() {
        updateState { it.copy(confirmPasswordVisible = !it.confirmPasswordVisible) }
    }

    fun onContinue() {
        val state = uiState.value
        if (state.isLoading) return

        val errors = mutableSetOf<LoginDetailsRequiredField>()
        if (state.email.isBlank()) errors += LoginDetailsRequiredField.EMAIL
        if (!state.passwordRequirements.isValid) errors += LoginDetailsRequiredField.PASSWORD
        if (state.password != state.confirmPassword) errors += LoginDetailsRequiredField.CONFIRM_PASSWORD

        if (errors.isNotEmpty()) {
            updateState { it.copy(requiredFieldErrors = errors) }
            val snack = if (LoginDetailsRequiredField.CONFIRM_PASSWORD in errors && state.password != state.confirmPassword) {
                sharedString(SR.strings.passwords_do_not_match)
            } else {
                sharedString(SR.strings.please_enter_all_required_fields)
            }
            showSnackbar(snack)
            return
        }

        if (sharedConfig.registeredCompanyCountryId == null) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        if (sharedConfig.registeredCompanyCityId == null) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        // Update shared config with actual data
        sharedConfig.email = state.email
        sharedConfig.password = state.password

        // Update current config in stack with updated shared config
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)

        scope.launch {
            updateState { it.copy(isLoading = true) }
                
                // Load company image if available
                val companyImageBytes = sharedConfig.companyImageFileName?.let { fileName ->
                    byteArrayFromFile(fileName)
                }
                
                commandBlock(showProgress = true) {
                    CommonCommands.generateMerchantVerificationCode(
                        GenerateMerchantVerificationCodeRequestModel(
                            // Company details (for merchants table)
                            companyName = sharedConfig.legalEntityName, // Will create translation key on server
                            email = state.email,
                            password = Security.hashPasswordForRequest(state.password),
                            dialCode = sharedConfig.companyDialCode, // Using company's dial code for merchant
                            phoneNumber = sharedConfig.companyPhoneNumber, // Using company's phone for merchant
                            bulstat = sharedConfig.bulstat,
                            iban = sharedConfig.iban,
                            countryId = sharedConfig.registeredCompanyCountryId!!, // Required, validated above
                            cityId = sharedConfig.registeredCompanyCityId!!, // Required, validated above
                            privacyPolicyAccepted = sharedConfig.privacyPolicyAccepted,
                            companyStreet = sharedConfig.registeredCompanyStreet, // maps to merchants.company_street (nullable)
                            companyImage = companyImageBytes, // Company logo/image
                            
                            // Company signatory details (for company_responsible_persons table) - convert to API format
                            signatories = sharedConfig.signatories.map { signatory ->
                                com.example.mystybag.sharedmodels.project.requestModels.merchant.SignatoryItem(
                                    firstName = signatory.firstName,
                                    lastName = signatory.lastName,
                                    phoneNumber = signatory.phoneNumber,
                                    dialCode = signatory.dialCode
                                )
                            }
                        )
                    )
                        .onResponse(
                            success = { response ->
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    navigate(
                                        NavigatorMerchantRegistrationStep5(
                                            merchantId = 0, // Will be set after verification
                                            email = response.email,
                                            password = state.password,
                                            signatories = sharedConfig.signatories,
                                            companyPhoneNumber = sharedConfig.companyPhoneNumber,
                                            companyDialCode = sharedConfig.companyDialCode,
                                            legalEntityName = sharedConfig.legalEntityName,
                                            bulstat = sharedConfig.bulstat,
                                            privacyPolicyAccepted = sharedConfig.privacyPolicyAccepted,
                                            registeredCompanyStreet = sharedConfig.registeredCompanyStreet,
                                            registeredCompanyCityId = sharedConfig.registeredCompanyCityId,
                                            registeredCompanyCityName = sharedConfig.registeredCompanyCityName,
                                            registeredCompanyCountryId = sharedConfig.registeredCompanyCountryId,
                                            companyImageFileName = sharedConfig.companyImageFileName,
                                            iban = sharedConfig.iban,
                                            paymentProvider = sharedConfig.paymentProvider,
                                            referralCode = sharedConfig.referralCode,
                                            phoneVerificationCode = sharedConfig.merchantPhoneVerificationCode
                                        )
                                    )
                                }
                            },
                            baseError = {
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(Response.getErrorMessage(it.status, it.apiEndPoint, it.statusMessage))
                                }
                            }
                        )
                }
            }
    }
}
